import React, { useState } from "react";
import { Plus, GitBranch, Play, Pause, MoreVertical, Clock } from "lucide-react";
import { CAPTURE_WORKFLOWS } from "@/data/mockData";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import WorkflowEditor from "@/components/workflow/WorkflowEditor";
import { toast } from "sonner";

export default function CaptureWorkflows() {
  const [workflows, setWorkflows] = useState(CAPTURE_WORKFLOWS);
  const [editing, setEditing] = useState(null);

  const toggleStatus = (id) => {
    setWorkflows((wfs) =>
      wfs.map((w) => (w.id === id ? { ...w, status: w.status === "active" ? "disabled" : "active" } : w))
    );
    toast.success("Workflow status updated");
  };

  const handleCreate = () => {
    const id = `wf_${Date.now()}`;
    const blank = {
      id,
      name: "Untitled Workflow",
      description: "Describe what this workflow does",
      status: "disabled",
      updated_at: new Date().toISOString(),
      runs_today: 0,
      success_rate: 0,
      nodes: [
        { id: "n1", type: "eventTrigger", position: { x: 100, y: 120 }, data: { label: "Event Trigger", eventType: "credit_card_requested" } },
      ],
      edges: [],
    };
    setWorkflows((w) => [blank, ...w]);
    setEditing(blank);
  };

  const handleSave = (updated) => {
    setWorkflows((wfs) => wfs.map((w) => (w.id === updated.id ? updated : w)));
    setEditing(updated);
  };

  if (editing) {
    return (
      <WorkflowEditor
        workflow={editing}
        onBack={() => setEditing(null)}
        onSave={handleSave}
      />
    );
  }

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps mb-2">Automation · {workflows.length} workflows</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Capture Workflows</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-xl">
            Build visual pipelines that react to incoming events. Drag activities, connect them, ship logic in minutes.
          </p>
        </div>
        <Button onClick={handleCreate} data-testid="workflows-create" className="gap-2 bg-primary hover:bg-[#0B2B4E] text-primary-foreground">
          <Plus className="w-4 h-4" /> New workflow
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {workflows.map((wf) => (
          <Card key={wf.id} data-testid={`workflow-card-${wf.id}`} className="p-5 border-border hover:border-primary/50 transition-colors group">
            <div className="flex items-start justify-between mb-3">
              <div className="w-9 h-9 rounded-md bg-primary/10 border border-primary/20 text-primary flex items-center justify-center">
                <GitBranch className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className={
                  wf.status === "active"
                    ? "border-emerald-500/40 text-emerald-500 bg-emerald-500/10"
                    : "border-muted-foreground/30 text-muted-foreground"
                }>
                  {wf.status === "active" ? <><Play className="w-2.5 h-2.5 mr-1" /> Active</> : <><Pause className="w-2.5 h-2.5 mr-1" /> Disabled</>}
                </Badge>
                <Switch
                  data-testid={`workflow-toggle-${wf.id}`}
                  checked={wf.status === "active"}
                  onCheckedChange={() => toggleStatus(wf.id)}
                />
              </div>
            </div>

            <div className="font-display text-lg font-semibold mb-1 line-clamp-1">{wf.name}</div>
            <p className="text-sm text-muted-foreground line-clamp-2 mb-4 min-h-[2.5rem]">{wf.description}</p>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <Meta label="Steps" value={wf.nodes.length} />
              <Meta label="Runs today" value={wf.runs_today.toLocaleString()} />
              <Meta label="Success" value={`${Math.round(wf.success_rate * 100)}%`} />
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-3">
              <span className="flex items-center gap-1.5">
                <Clock className="w-3 h-3" />
                {new Date(wf.updated_at).toLocaleDateString()}
              </span>
              <Button
                data-testid={`workflow-edit-${wf.id}`}
                variant="ghost"
                size="sm"
                onClick={() => setEditing(wf)}
                className="h-7 text-xs hover:text-primary"
              >
                Open editor →
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

const Meta = ({ label, value }) => (
  <div>
    <div className="label-caps text-[0.55rem]">{label}</div>
    <div className="font-display text-lg font-semibold mt-0.5">{value}</div>
  </div>
);
